<template>
  <div class="create-role-page py-3">
    <header class="create-role-toolbar">
      <button type="button" class="back-btn" @click="goBack" aria-label="Back to roles">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="19" y1="12" x2="5" y2="12"/>
          <polyline points="12 19 5 12 12 5"/>
        </svg>
        <span>Roles</span>
      </button>
      <div class="create-role-toolbar__title">
        <span class="create-role-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Create Role</h1>
          <p>Add a new role that can be assigned to users</p>
        </div>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="create-role-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="create-role-alert">{{ successMessage }}</CAlert>

    <section class="create-role-card">
      <header class="create-role-card__head">
        <h2>Role details</h2>
        <p>Use a short, lowercase identifier (e.g. <code>warehouse_lead</code>). Spaces are not allowed.</p>
      </header>

      <form class="create-role-form" @submit.prevent="submit">
        <div class="form-field">
          <label for="role-name" class="form-label">
            Name
            <span class="form-label__required" aria-hidden="true">*</span>
          </label>
          <input
            id="role-name"
            v-model.trim="form.name"
            type="text"
            class="form-input"
            placeholder="e.g. warehouse_lead"
            autocomplete="off"
            :disabled="isSubmitting"
            :class="{ 'has-error': fieldErrors.name }"
            @input="clearFieldError('name')"
          />
          <span v-if="fieldErrors.name" class="form-hint form-hint--error">{{ fieldErrors.name }}</span>
          <span v-else class="form-hint">Letters, numbers and underscores only.</span>
        </div>

        <div class="form-actions">
          <button type="button" class="cancel-btn" :disabled="isSubmitting" @click="goBack">
            Cancel
          </button>
          <button type="submit" class="submit-btn" :disabled="isSubmitting || !canSubmit">
            <CSpinner v-if="isSubmitting" size="sm" class="submit-btn__spinner" />
            <span>{{ isSubmitting ? 'Creating…' : 'Create Role' }}</span>
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

const NAME_PATTERN = /^[a-zA-Z0-9_]+$/

export default {
  name: 'SettingsCreateRole',
  data () {
    return {
      form: {
        name: ''
      },
      fieldErrors: {},
      errorMessage: '',
      successMessage: '',
      isSubmitting: false
    }
  },
  computed: {
    canSubmit () {
      return this.form.name.length > 0
    }
  },
  methods: {
    clearFieldError (field) {
      if (this.fieldErrors[field]) {
        this.$delete(this.fieldErrors, field)
      }
      this.errorMessage = ''
    },
    validate () {
      const errors = {}
      const name = this.form.name.trim()

      if (!name) {
        errors.name = 'Role name is required.'
      } else if (!NAME_PATTERN.test(name)) {
        errors.name = 'Use letters, numbers and underscores only — no spaces.'
      }

      this.fieldErrors = errors
      return Object.keys(errors).length === 0
    },
    submit () {
      if (!this.validate()) return

      this.isSubmitting = true
      this.errorMessage = ''
      this.successMessage = ''

      const token = localStorage.getItem('api_token')
      const payload = { name: this.form.name.trim() }

      axios.post(`${this.$apiAdress}/api/role?token=${token}`, payload)
        .then((response) => {
          const validationErrors = response.data?.error
          if (validationErrors && typeof validationErrors === 'object') {
            this.fieldErrors = this.flattenValidationErrors(validationErrors)
            this.errorMessage = 'Please fix the errors below.'
            return
          }

          this.successMessage = response.data?.message || 'Role created successfully.'
          this.form.name = ''
          setTimeout(() => {
            this.$router.push('/settings/roles')
          }, 600)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message
            || 'Unable to create role. You may not have permission to create roles.'
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },
    flattenValidationErrors (errors) {
      const flat = {}
      Object.keys(errors).forEach((key) => {
        const value = errors[key]
        flat[key] = Array.isArray(value) ? value[0] : String(value)
      })
      return flat
    },
    goBack () {
      this.$router.push('/settings/roles')
    }
  }
}
</script>

<style scoped>
.create-role-page {
  padding: 0.25rem 0;
  max-width: 720px;
}

/* ── Toolbar ── */
.create-role-toolbar {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.25rem 0.25rem 1rem;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.55rem;
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;
}

.back-btn:hover {
  background: #f5fbf8;
  color: #0e5b46;
}

.create-role-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.create-role-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.create-role-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.create-role-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

/* ── Alert ── */
.create-role-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Card ── */
.create-role-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.create-role-card__head {
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.create-role-card__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.create-role-card__head p {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.create-role-card__head code {
  background: #eef1f7;
  color: #1f2a44;
  padding: 0.05rem 0.35rem;
  border-radius: 4px;
  font-size: 0.78rem;
}

/* ── Form ── */
.create-role-form {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.form-label__required {
  color: #b45309;
  margin-left: 0.15rem;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.85rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.form-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.form-input.has-error {
  border-color: #dc2626;
}

.form-input.has-error:focus {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
}

.form-hint {
  font-size: 0.74rem;
  color: #6b7280;
}

.form-hint--error {
  color: #b91c1c;
  font-weight: 600;
}

/* ── Actions ── */
.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  padding-top: 0.5rem;
  border-top: 1px solid #eef1f7;
  margin-top: 0.5rem;
}

.cancel-btn {
  height: 38px;
  padding: 0 1.1rem;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cancel-btn:hover:not(:disabled) {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 38px;
  padding: 0 1.25rem;
  border-radius: 8px;
  border: 0;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.12);
}

.submit-btn__spinner {
  color: #ffffff;
}

/* ── Responsive ── */
@media (max-width: 575px) {
  .create-role-page {
    padding: 0 0 0.5rem;
  }
  .create-role-toolbar {
    flex-wrap: wrap;
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .create-role-toolbar__accent {
    height: 30px;
  }
  .create-role-toolbar__title h1 {
    font-size: 1rem;
  }
  .create-role-toolbar__title p {
    font-size: 0.72rem;
  }
  .create-role-card__head {
    padding: 0.75rem 0.85rem;
  }
  .create-role-form {
    padding: 0.85rem;
  }
  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 0.5rem;
  }

  .cancel-btn,
  .submit-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
